import { Award, Medal, Trophy } from 'lucide-react';

interface TierConfig {
  enabled?: boolean;
  gold_threshold?: number;
  silver_threshold?: number;
  bronze_threshold?: number;
}

interface TierThresholdsProps {
  tier_config?: TierConfig | null;
  passing_score?: number;
  className?: string;
  /** compact: simple tier rows (preview); detailed: medal cards (start exam) */
  variant?: 'compact' | 'detailed';
}

const TIERS = [
  {
    key: 'gold_threshold', label: 'Gold',
    classes: 'bg-yellow-50 border-yellow-200 text-yellow-700', icon: 'text-yellow-500',
    medal: 'from-yellow-300 to-amber-500',
    card: 'border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50', text: 'text-amber-700',
  },
  {
    key: 'silver_threshold', label: 'Silver',
    classes: 'bg-gray-50 border-gray-300 text-gray-700', icon: 'text-gray-400',
    medal: 'from-gray-200 to-slate-400',
    card: 'border-slate-200 bg-gradient-to-br from-slate-50 to-gray-50', text: 'text-slate-700',
  },
  {
    key: 'bronze_threshold', label: 'Bronze',
    classes: 'bg-orange-50 border-orange-200 text-orange-700', icon: 'text-orange-500',
    medal: 'from-orange-300 to-amber-700',
    card: 'border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50', text: 'text-orange-700',
  },
] as const;

/**
 * Score needed for each certificate tier; renders nothing when tiers are disabled.
 * A tier is only awarded on a pass, so each threshold is shown as at least the passing score.
 */
export default function TierThresholds({ tier_config, passing_score = 0, className = '', variant = 'detailed' }: TierThresholdsProps) {
  if (!tier_config?.enabled) return null;

  const tiers = TIERS.map(tier => ({
    ...tier,
    threshold: Math.min(100, Math.max(Number(tier_config[tier.key]) || 0, passing_score)),
  }));

  // Each tier runs from its threshold up to the next higher tier; empty ranges can't be earned, so hide them
  const reachable_tiers = tiers
    .map((tier, index) => ({ ...tier, upper: index === 0 ? null : tiers[index - 1].threshold }))
    .filter(tier => tier.upper === null || tier.threshold < tier.upper);

  const rangeText = (tier: { threshold: number; upper: number | null }) =>
    tier.upper === null ? `Score ${tier.threshold}% or above` : `Score ${tier.threshold}% to below ${tier.upper}%`;

  if (variant === 'compact') {
    return (
      <div className={className}>
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Award className="w-5 h-5 text-purple-600" />
          Certificate Tiers
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          Pass with {passing_score}% or more. Your final score decides which certificate tier you earn.
        </p>
        <div className={`grid grid-cols-1 gap-3 ${reachable_tiers.length === 3 ? 'sm:grid-cols-3' : reachable_tiers.length === 2 ? 'sm:grid-cols-2' : ''}`}>
          {reachable_tiers.map(tier => (
            <div key={tier.key} className={`flex items-center gap-3 p-3 rounded-lg border ${tier.classes}`}>
              <Award className={`w-6 h-6 flex-shrink-0 ${tier.icon}`} />
              <div>
                <p className="text-sm font-semibold">{tier.label}</p>
                <p className="text-xs">{rangeText(tier)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden ${className}`}>
      <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
          <Trophy className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Certificate Tiers</h3>
          <p className="text-sm text-gray-600">
            Pass with {passing_score}% or more. Your final score decides which certificate tier you earn.
          </p>
        </div>
      </div>

      <div className="p-5">
        <div className={`grid grid-cols-1 gap-4 ${reachable_tiers.length === 3 ? 'sm:grid-cols-3' : reachable_tiers.length === 2 ? 'sm:grid-cols-2' : ''}`}>
          {reachable_tiers.map(tier => (
            <div key={tier.key} className={`relative rounded-xl border p-4 ${tier.card}`}>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${tier.medal} shadow-md flex items-center justify-center flex-shrink-0 ring-4 ring-white`}>
                  <Medal className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${tier.text}`}>{tier.label}</p>
                  <p className="text-2xl font-bold text-gray-900 leading-tight">{tier.threshold}%+</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-600">
                {rangeText(tier)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
